import { useAtomValue } from "jotai";

import {
  accidentResponsibilityAtom,
  otherVehicleCircumstanceAtom,
  vehicleCircumstancePhAtom,
} from "atoms/accidentCircumstance";
import {
  amicableSignedAtom,
  authoritiesInterventionAtom,
  driverPresentAtom,
  informationOnOtherVehicleAtom,
  numInjuredPeopleAtom,
  numVehiclesInvolvedAtom,
} from "atoms/accidentInformation";
import { cityOfAccidentAtom } from "atoms/accidentLocation";
import { additionalDetailsAtom } from "atoms/additionalDetails";
import { claimHandlingDecisionAtom } from "atoms/claimHandlingDecision";
import {
  authoritiesUploadedPhotosAtom,
  caiUploadedPhotosAtom,
  injuredUploadedPhotosAtom,
} from "atoms/digitalJourneyPhotos";
import { injuredPersonsAtom } from "atoms/injuredPersons";
import { insuranceTypeToActivateAtom } from "atoms/insuranceTypeToActivate";
import { naturalEventAtom } from "atoms/naturalEvents";
import {
  otherDriverFirstNameAtom,
  otherDriverLastNameAtom,
  otherDriverPersonalCodeAtom,
  otherVehicleInsuranceAtom,
  otherVehicleLicensePlateAtom,
  otherVehicleTypeAtom,
} from "atoms/otherVehicleInformation";
import { firstNamePhAtom, lastNamePhAtom, personalCodePhAtom } from "atoms/phPersonalDetails";
import { uploadedPhotosAtom } from "atoms/photosUpload";
import { otherVehiclePointsOfImpactAtom, pointsOfImpactPhAtom } from "atoms/pointsOfImpactDigitalJourney";
import { registrationAtom } from "atoms/registrationPlate";
import { bodyShopAtom, bodyShopPostalCodeAtom } from "atoms/selectBodyShop";
import { serverSessionAtom } from "atoms/serverSession";
import { dateOfAccidentAtom, timeOfAccidentAtom } from "atoms/timeOfAccident";
import { typeOfClaimAtom } from "atoms/typeOfClaim";
import { vandalismSeverityAtom } from "atoms/vandalismSeverity";
import { mileageAtom } from "atoms/vehicleCondition/mileage";
import { vehicleConditionQuestionsAtom } from "atoms/vehicleCondition/questions";
import { vehicleTypeAtom } from "atoms/vehicleType";
import { vinAtom } from "atoms/vin/vin";
import { windowsPOIAtom } from "atoms/windowsPOI";
import { BodyParts } from "utils/generateClassifyBody/generateClassifyBody";
import { removeNumberSeparators } from "utils/stringHelper";
import { signatureImageAtom } from "atoms/signature";

export const useDigitalJourneyInformation = (): BodyParts => {
  const serverSession = useAtomValue(serverSessionAtom);
  const firstNamePh = useAtomValue(firstNamePhAtom);
  const lastNamePh = useAtomValue(lastNamePhAtom);
  const personalCodePh = useAtomValue(personalCodePhAtom);
  const dateOfAccident = useAtomValue(dateOfAccidentAtom);
  const timeOfAccident = useAtomValue(timeOfAccidentAtom);
  const numVehiclesInvolved = useAtomValue(numVehiclesInvolvedAtom);
  const driverPresent = useAtomValue(driverPresentAtom);
  const informationOnOtherVehicle = useAtomValue(informationOnOtherVehicleAtom);
  const numInjuredPeople = useAtomValue(numInjuredPeopleAtom);
  const cai = useAtomValue(amicableSignedAtom);
  const authoritiesIntervention = useAtomValue(authoritiesInterventionAtom);
  const selectedBodyShop = useAtomValue(bodyShopAtom);
  const cityOfAccident = useAtomValue(cityOfAccidentAtom);
  const vehicleCircumstancePh = useAtomValue(vehicleCircumstancePhAtom);
  const otherVehicleCircumstance = useAtomValue(otherVehicleCircumstanceAtom);
  const accidentResponsibility = useAtomValue(accidentResponsibilityAtom);
  const claimHandlingDecision = useAtomValue(claimHandlingDecisionAtom);
  const mileage = useAtomValue(mileageAtom);
  const vehicleQuestions = useAtomValue(vehicleConditionQuestionsAtom);
  const injuredPersons = useAtomValue(injuredPersonsAtom);
  const otherVehicleLicensePlate = useAtomValue(otherVehicleLicensePlateAtom);
  const otherVehicleType = useAtomValue(otherVehicleTypeAtom);
  const otherVehicleInsurance = useAtomValue(otherVehicleInsuranceAtom);
  const otherDriverFirstName = useAtomValue(otherDriverFirstNameAtom);
  const otherDriverLastName = useAtomValue(otherDriverLastNameAtom);
  const otherDriverPersonalCode = useAtomValue(otherDriverPersonalCodeAtom);
  const pointsOfImpactPh = useAtomValue(pointsOfImpactPhAtom);
  const otherVehiclePointsOfImpact = useAtomValue(otherVehiclePointsOfImpactAtom);
  const accidentType = useAtomValue(typeOfClaimAtom);
  const vehicleTypePh = useAtomValue(vehicleTypeAtom);
  const licensePlatePh = useAtomValue(registrationAtom);
  const additionalDetails = useAtomValue(additionalDetailsAtom);
  const postalCodePh = useAtomValue(bodyShopPostalCodeAtom);
  const caiUploadedPhotos = useAtomValue(caiUploadedPhotosAtom);
  const injuredUploadedPhotos = useAtomValue(injuredUploadedPhotosAtom);
  const authoritiesUploadedPhotos = useAtomValue(authoritiesUploadedPhotosAtom);
  const pointsOfImpactGlass = useAtomValue(windowsPOIAtom);
  const vandalismSeverity = useAtomValue(vandalismSeverityAtom);
  const naturalEventType = useAtomValue(naturalEventAtom);
  const insuranceType = useAtomValue(insuranceTypeToActivateAtom);

  const signatureImage = useAtomValue(signatureImageAtom);

  const caiPhotos = Object.values(caiUploadedPhotos).map((photo) => ({
    clientImageId: photo.clientImageId,
    format: photo.format,
    path: photo.path,
    bucket: photo.bucket,
    key: photo.key,
  }));

  const hospitalReportPhotos = Object.values(injuredUploadedPhotos).map((photo) => ({
    clientImageId: photo.clientImageId,
    format: photo.format,
    path: photo.path,
    bucket: photo.bucket,
    key: photo.key,
  }));

  const authoritiesPhotos = Object.values(authoritiesUploadedPhotos).map((photo) => ({
    clientImageId: photo.clientImageId,
    format: photo.format,
    path: photo.path,
    bucket: photo.bucket,
    key: photo.key,
  }));

  const getAccidentDateAndTime = () => {
    const accidentDate = new Date();
    if (
      timeOfAccident instanceof Date &&
      !isNaN(timeOfAccident.getTime()) &&
      dateOfAccident instanceof Date &&
      !isNaN(dateOfAccident.getTime())
    ) {
      return getDateAndTime(dateOfAccident, timeOfAccident);
    }

    if (timeOfAccident && typeof timeOfAccident === "string" && dateOfAccident && typeof dateOfAccident === "string") {
      const timeOfAccidentDate = new Date(timeOfAccident);
      const dateOfAccidentDate = new Date(dateOfAccident);
      if (!isNaN(timeOfAccidentDate.getTime()) && !isNaN(dateOfAccidentDate.getTime())) {
        return getDateAndTime(dateOfAccidentDate, timeOfAccidentDate);
      }
    }

    return accidentDate.toISOString();
  };

  const getDateAndTime = (date: Date, time: Date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();

    const hours = time.getHours();
    const minutes = time.getMinutes();
    const seconds = time.getSeconds();
    const milliseconds = time.getMilliseconds();

    const accidentDateTime = new Date(year, month, day, hours, minutes, seconds, milliseconds);
    return accidentDateTime.toISOString();
  };

  const uploadedPhotos = useAtomValue(uploadedPhotosAtom);
  const vinInput = useAtomValue(vinAtom);

  const signatureUploaded = signatureImage ? uploadedPhotos[signatureImage.id] : null;

  return {
    uploadedPhotos,
    vehicleConditionQuestions: {
      haveAirbagsDeployed: vehicleQuestions.haveAirbagsDeployed,
      isVehicleDrivable: !vehicleQuestions.isVehicleDrivable,
    },
    vinInput,
    pointsOfImpact: pointsOfImpactPh.filter((poi) => poi !== "Roof"),
    mileage,
    digitalJourney: {
      firstNamePh,
      lastNamePh,
      licensePlatePh,
      personalCodePh,
      mileage: parseInt(removeNumberSeparators(mileage)),
      phoneNumberPh: serverSession?.digitalJourney?.phoneNumberPh || "",
      emailPh: serverSession?.digitalJourney?.emailPh || "",
      accidentType,
      vandalismSeverity,
      naturalEventType,
      insuranceType,
      driverPresent,
      informationOnOtherVehicle,
      informationOnOtherDriver: informationOnOtherVehicle,
      claimHandlingDecision,
      cai,
      authoritiesIntervention,
      accidentResponsibility,
      additionalDetails,
      numVehiclesInvolved,
      vehicleTypePh,
      pointsOfImpactPh,
      airbagDeployed: vehicleQuestions.haveAirbagsDeployed,
      isDrivable: vehicleQuestions.isVehicleDrivable,
      pointsOfImpactGlass,
      vehicleCircumstancePh,
      otherDriverFirstName,
      otherDriverLastName,
      otherDriverPersonalCode,
      otherVehicleInsurance,
      otherVehicleLicensePlate,
      otherVehiclePointsOfImpact,
      otherVehicleCircumstance,
      otherVehicleType,
      caiPhotos,
      hospitalReportPhotos,
      authoritiesPhotos,
      injuredPersons: injuredPersons.filter(
        (injuredPerson) => !!injuredPerson.firstName && !!injuredPerson.lastName && !!injuredPerson.role
      ),
      numInjuredPeople,
      accidentLocationId: cityOfAccident?.id,
      accidentDateTime: getAccidentDateAndTime(),
      bodyShopId: selectedBodyShop?.id,
      postalCodePh,
      signaturePhoto: signatureUploaded ? {
        format: signatureUploaded.format,
        path: signatureUploaded.path,
        bucket: signatureUploaded.bucket,
        key: signatureUploaded.key,
        clientImageId: signatureUploaded.clientImageId,
      } : null,
    },
  };
};
